import { createSlice } from '@reduxjs/toolkit';
import {
    approveExpenseVoucher,
    createExpenseCategory,
    createExpenseVoucher,
    deleteExpenseCategory,
    deleteExpenseVoucher,
    getExpenseCategories,
    getExpenseVouchers,
    updateExpenseCategory,
} from './thunk';

export interface ExpenseCategory {
  id: number;
  name: string;
  description?: string;
  active?: boolean;
}

export interface ExpenseVoucher {
  id: number;
  voucherNo?: string;
  amount: number;
  reason: string;
  status: 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | string;
  createdBy?: string;
  createdDate?: string;
  approvedBy?: string | null;
  approvedAt?: string | null;
  approvalNote?: string | null;
  category?: { id: number; name?: string };
}

interface ExpenseState {
  categories: ExpenseCategory[];
  categoriesTotal: number;
  vouchers: ExpenseVoucher[];
  vouchersTotal: number;
  loading: boolean;
  creating: boolean;
  error: string | null;
}

const initialState: ExpenseState = {
  categories: [],
  categoriesTotal: 0,
  vouchers: [],
  vouchersTotal: 0,
  loading: false,
  creating: false,
  error: null,
};

const expenseSlice = createSlice({
  name: 'expense',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getExpenseCategories.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getExpenseCategories.fulfilled, (state, action: any) => {
      state.loading = false;
      state.categories = Array.isArray(action.payload?.data) ? action.payload.data : [];
      state.categoriesTotal = Number(action.payload?.total || 0);
    });
    builder.addCase(getExpenseCategories.rejected, (state, action: any) => {
      state.loading = false;
      state.error = action.payload?.message || action.error?.message || 'Load categories failed';
    });

    builder.addCase(getExpenseVouchers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getExpenseVouchers.fulfilled, (state, action: any) => {
      state.loading = false;
      state.vouchers = Array.isArray(action.payload?.data) ? action.payload.data : [];
      state.vouchersTotal = Number(action.payload?.total || 0);
    });
    builder.addCase(getExpenseVouchers.rejected, (state, action: any) => {
      state.loading = false;
      state.error = action.payload?.message || action.error?.message || 'Load vouchers failed';
    });

    builder.addCase(createExpenseCategory.pending, (state) => {
      state.creating = true;
      state.error = null;
    });
    builder.addCase(createExpenseCategory.fulfilled, (state, action: any) => {
      state.creating = false;
      if (action.payload) {
        state.categories = [action.payload, ...state.categories];
      }
    });
    builder.addCase(createExpenseCategory.rejected, (state, action: any) => {
      state.creating = false;
      state.error = action.payload?.message || action.error?.message || 'Create category failed';
    });

    builder.addCase(updateExpenseCategory.fulfilled, (state, action: any) => {
      const updated = action.payload;
      if (!updated?.id) return;
      state.categories = state.categories.map((item) =>
        item.id === updated.id ? { ...item, ...updated } : item,
      );
    });

    builder.addCase(deleteExpenseCategory.fulfilled, (state, action: any) => {
      state.categories = state.categories.filter((item) => item.id !== action.payload);
    });

    builder.addCase(createExpenseVoucher.pending, (state) => {
      state.creating = true;
      state.error = null;
    });
    builder.addCase(createExpenseVoucher.fulfilled, (state, action: any) => {
      state.creating = false;
      if (action.payload) {
        state.vouchers = [action.payload, ...state.vouchers];
      }
    });
    builder.addCase(createExpenseVoucher.rejected, (state, action: any) => {
      state.creating = false;
      state.error = action.payload?.message || action.error?.message || 'Create voucher failed';
    });

    builder.addCase(deleteExpenseVoucher.fulfilled, (state, action: any) => {
      state.vouchers = state.vouchers.filter((item) => item.id !== action.payload);
    });

    builder.addCase(approveExpenseVoucher.fulfilled, (state, action: any) => {
      const approvedItem = action.payload;
      if (!approvedItem || !approvedItem.id) return;
      state.vouchers = state.vouchers.map((item) =>
        item.id === approvedItem.id ? { ...item, ...approvedItem } : item,
      );
    });
  },
});

export default expenseSlice.reducer;
